/**
 * authSessionReconcile — the Supabase session is the truth about who is
 * signed in; the user store is a cache of it.
 *
 * Until this existed, `RequireAuth` only asked the user store, and the store
 * was hydrated from localStorage. Nothing listened to `onAuthStateChange`, so
 * an expired or revoked session left a "signed-in" shell whose every query
 * failed under RLS, and a valid session with cleared storage bounced to the
 * login page. This module is the pure decision; `AuthSessionSync` applies it.
 */

export const UNKNOWN_USER_ID = 'unknown';

/** The slice of a Supabase session this decision needs. */
export interface SessionLike {
  user: {
    id: string;
    email?: string | null;
    user_metadata?: { full_name?: string | null; name?: string | null } | null;
  };
}

/** The slice of the user store this decision reads. */
export interface UserStateLike {
  userId: string;
  isImpersonating: boolean;
}

export type ReconcileAction =
  | { kind: 'none' }
  /** Session gone while the store still thinks someone is signed in. */
  | { kind: 'sign_out' }
  /** Session present, store cold (cleared storage, another device). */
  | { kind: 'adopt_session'; userId: string; userName: string; userEmail: string }
  /**
   * The session names a different person than the one this page loaded data
   * for (signed in as someone else without a reload, directly or after a
   * sign-out, here or in another tab). Every workspace-scoped store still holds
   * the previous person's rows, so the page starts over (lib/accountChange.ts).
   */
  | { kind: 'switch_account'; userId: string; userName: string; userEmail: string };

export interface ReconcileInput {
  session: SessionLike | null;
  state: UserStateLike;
  /** Demo mode signs in a synthetic user with no Supabase session; leave it alone. */
  demoMode: boolean;
  /** The auth user this page has loaded data for, if any (lib/accountChange.ts). */
  pageUserId?: string | null;
}

export function displayNameFromSession(session: SessionLike): string {
  const meta = session.user.user_metadata ?? undefined;
  const fromMeta = (meta?.full_name ?? meta?.name ?? '').trim();
  if (fromMeta) return fromMeta;
  const email = session.user.email ?? '';
  const local = email.split('@')[0]?.trim();
  return local ? local.replace(/[._]/g, ' ') : 'User';
}

export function reconcileSession(input: ReconcileInput): ReconcileAction {
  const { session, state, demoMode, pageUserId } = input;
  // Checked before the store: the store may already name the new person (the
  // sign-in page sets it) while every other store still holds the old one's rows.
  if (
    session &&
    !demoMode &&
    !state.isImpersonating &&
    pageUserId &&
    pageUserId !== session.user.id
  ) {
    return {
      kind: 'switch_account',
      userId: session.user.id,
      userName: displayNameFromSession(session),
      userEmail: session.user.email ?? '',
    };
  }
  return reconcileStore(input);
}

function reconcileStore({ session, state, demoMode }: ReconcileInput): ReconcileAction {
  if (demoMode) return { kind: 'none' };

  const storeSignedIn = Boolean(state.userId) && state.userId !== UNKNOWN_USER_ID;

  if (!session) {
    return storeSignedIn ? { kind: 'sign_out' } : { kind: 'none' };
  }

  // Impersonation deliberately makes the store's userId differ from the
  // session's; that is not drift to correct.
  if (state.isImpersonating) return { kind: 'none' };

  if (!storeSignedIn) {
    return {
      kind: 'adopt_session',
      userId: session.user.id,
      userName: displayNameFromSession(session),
      userEmail: session.user.email ?? '',
    };
  }

  // Store names a different real user than the session (e.g. a second account
  // signed in from another tab). The session wins.
  if (state.userId !== session.user.id) {
    return {
      kind: 'adopt_session',
      userId: session.user.id,
      userName: displayNameFromSession(session),
      userEmail: session.user.email ?? '',
    };
  }

  return { kind: 'none' };
}
